'use client';

import { useCallback, useMemo, useState } from 'react';

import { TypedDocumentNode } from '@apollo/client';
import { QueryHookOptions, QueryResult, useQuery } from '@apollo/client/react';
import { format } from 'date-fns';

export type UsePaginatedQueryResult<
  TData extends Record<string, any>,
  TVariables extends {
    offset?: number | null;
    limit?: number | null;
  },
> = {
  hasNext: boolean;
  count?: number;
  list: any;
  fetchMore: (limit?: number) => void;
  listResult: QueryResult<TData, TVariables>;
  loading: boolean;
  called: boolean;
  isReadyToFetchMore: boolean;
};

const getChild = (data: Record<string, any>) => {
  if (!data) {
    return undefined;
  }

  const keys = Object.keys(data);
  return data[keys[0]];
};

export const usePaginatedQuery = <
  TData extends Record<string, any>,
  TVariables extends {
    offset?: number | null;
    limit?: number | null;
  },
>(
  queryList: TypedDocumentNode<TData, TVariables>,
  queryCount?: TypedDocumentNode<
    Record<string, { aggregate?: { count?: number } }>,
    Omit<TVariables, 'limit' | 'offset'>
  >,
  {
    variables: { offset = 0, limit = 5, ...variables } = {} as TVariables,
    ...options
  }: QueryHookOptions<TData, TVariables> = {} as QueryHookOptions<
    TData,
    TVariables
  >,
  force?: 'force-today' | 'force-not-today'
): UsePaginatedQueryResult<TData, TVariables> => {
  const [hasMore, setHasMore] = useState(true);

  const countResult = useQuery(queryCount || (queryList as any), {
    variables: variables as Omit<TVariables, 'limit' | 'offset'>,
    skip: !queryCount,
  });

  const listResult = useQuery(queryList, {
    ...options,
    variables: { ...variables, offset, limit } as TVariables,
    notifyOnNetworkStatusChange: true,
  });

  const count = queryCount
    ? getChild(countResult?.data as any)?.aggregate?.count || 0
    : undefined;

  const rawList = getChild(listResult.data as any) || [];

  const list = useMemo(() => {
    let filteredList = rawList;
    const today = format(new Date(), 'yyyy-MM-dd');
    if (force === 'force-today') {
      filteredList = filteredList?.filter((event: any) => event.date === today);
    } else if (force === 'force-not-today') {
      filteredList = filteredList?.filter((event: any) => event.date !== today);
    }
    return filteredList;
  }, [rawList, force]);

  const fetchMore = useCallback(
    (pageLimit = 10) => {
      if (listResult.loading) return;

      listResult.fetchMore({
        variables: {
          ...variables,
          offset: (offset || 0) + rawList.length,
          limit: pageLimit,
        } as Partial<TVariables>,
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const key = Object.keys(fetchMoreResult)[0];
          const newItems = fetchMoreResult[key];

          if (newItems.length < pageLimit) {
            setHasMore(false);
          }

          return {
            ...prev,
            [key]: [...(prev[key] || []), ...newItems],
          };
        },
      });
    },
    [listResult, rawList.length, variables, offset]
  );

  const hasNext = count !== undefined ? rawList.length < count : hasMore;

  return useMemo(
    () => ({
      hasNext,
      count,
      list,
      fetchMore,
      listResult,
      loading: listResult.loading,
      called: true,
      isReadyToFetchMore: !listResult.loading,
    }),
    [hasNext, count, list, fetchMore, listResult]
  );
};
